import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { formatDateHyphens } from '../../../../../utils'

function RepeatOrderModal({ open, onClose, onSave, values, repeatOrder }) {
  const frequencyOptions = [
    { label: 'Daily', value: 'DAILY' },
    { label: 'Weekly', value: 'WEEKLY' },
    { label: 'Monthly on a Specific Day', value: 'MONTHLY' },
  ]

  const daysOfWeek = [
    { label: 'Sunday', value: 'SUN' },
    { label: 'Monday', value: 'MON' },
    { label: 'Tuesday', value: 'TUE' },
    { label: 'Wednesday', value: 'WED' },
    { label: 'Thursday', value: 'THU' },
    { label: 'Friday', value: 'FRI' },
    { label: 'Saturday', value: 'SAT' },
  ]

  const validationSchema = yup.object({
    frequency: yup
      .string('Select a frequency')
      .required('Frequency is required'),
    startDate: yup
      .date('Enter a valid date')
      .required('Start date is required'),
    endDate: yup
      .date('Enter a valid date')
      .nullable()
      .when(['startDate', 'frequency'], (startDate, frequency, schema) => {
        if (frequency !== 'ONCE') {
          return (
            startDate &&
            schema.min(startDate, 'End date must be after start date')
          )
        }
        return schema.nullable()
      }),
    repeatDays: yup.array().when('frequency', {
      is: 'WEEKLY',
      then: yup.array().min(1, 'Select at least one day for weekly repeat'),
    }),
    monthlyDay: yup.number().when('frequency', {
      is: 'MONTHLY',
      then: yup
        .number()
        .min(1, 'Must be at least 1')
        .max(31, 'Must be at most 28')
        .required('Day of the month is required'),
    }),
  })

  const formik = useFormik({
    initialValues: {
      frequency: repeatOrder?.frequency ?? 'DAILY',
      startDate:
        formatDateHyphens(repeatOrder?.start_date) ||
        new Date().toISOString().substring(0, 10),
      endDate: formatDateHyphens(repeatOrder?.end_date) || '',
      repeatDays: repeatOrder?.repeat_days?.split(',') ?? [],
      monthlyDay: repeatOrder?.monthly_day ?? 1,
    },
    validationSchema: validationSchema,
    onSubmit: repeatOrderSettings => {
      onSave({
        repeatOrderSettings,
        values,
        repeatOrderId: repeatOrder ? repeatOrder.id : null,
      })
      onClose()
    },
  })

  const handleDayChange = event => {
    const { value } = event.target
    const currentIndex = formik.values.repeatDays.indexOf(value)
    const newChecked = [...formik.values.repeatDays]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    formik.setFieldValue('repeatDays', newChecked)
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Schedule Repeating Order</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel id="frequency-label">Frequency</InputLabel>
          <Select
            labelId="frequency-label"
            label="Frequency"
            value={formik.values.frequency}
            onChange={formik.handleChange('frequency')}
            error={formik.touched.frequency && Boolean(formik.errors.frequency)}
          >
            {frequencyOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {formik.values.frequency === 'WEEKLY' && (
          <FormControl
            component="fieldset"
            error={
              formik.touched.repeatDays && Boolean(formik.errors.repeatDays)
            }
          >
            <FormGroup>
              {daysOfWeek.map(day => (
                <FormControlLabel
                  key={day.value}
                  control={
                    <Checkbox
                      checked={formik.values.repeatDays.includes(day.value)}
                      onChange={handleDayChange}
                      value={day.value}
                    />
                  }
                  label={day.label}
                />
              ))}
            </FormGroup>
          </FormControl>
        )}

        {formik.values.frequency === 'MONTHLY' && (
          <TextField
            label="Day of the Month"
            type="number"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            {...formik.getFieldProps('monthlyDay')}
            error={
              formik.touched.monthlyDay && Boolean(formik.errors.monthlyDay)
            }
            helperText={formik.touched.monthlyDay && formik.errors.monthlyDay}
          />
        )}
        <TextField
          label={formik.values.frequency !== 'ONCE' ? 'Start Date' : 'Date'}
          type="date"
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          {...formik.getFieldProps('startDate')}
          error={formik.touched.startDate && Boolean(formik.errors.startDate)}
          helperText={formik.touched.startDate && formik.errors.startDate}
        />
        {formik.values.frequency !== 'ONCE' && (
          <TextField
            label="End Date (optional)"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            {...formik.getFieldProps('endDate')}
            error={formik.touched.endDate && Boolean(formik.errors.endDate)}
            helperText={formik.touched.endDate && formik.errors.endDate}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={formik.handleSubmit} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RepeatOrderModal
